/**
 * Field form loại lịch hẹn (catalog hoặc quick-create). Dùng trong FormProvider.
 */
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CharacterCount,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@/shared/ui';
import AppointmentTypeColorField from './AppointmentTypeColorField';
import {
  APPOINTMENT_TYPE_NAME_MAX_LENGTH,
  durationSelectOptions,
  type AppointmentTypeFormValues,
} from '../utils/appointmentTypeForm';
const selectIdleIconSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
};

const CatalogStatusOption = ({ active, label }: { active: boolean; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: active ? 'success.main' : 'text.disabled',
        flexShrink: 0,
      }}
    />
    {label}
  </Box>
);

export interface AppointmentTypeFormFieldsProps {
  mode: 'catalog' | 'quick';
  isEdit: boolean;
  disabled?: boolean;
}

const AppointmentTypeFormFields: React.FC<AppointmentTypeFormFieldsProps> = ({
  mode,
  isEdit,
  disabled = false,
}) => {
  const { t } = useTranslation('appointment');
  const { control } = useFormContext<AppointmentTypeFormValues>();
  const showPolicy = mode === 'catalog';

  const durationField = (
    <Controller
      name="defaultDuration"
      control={control}
      render={({ field }) => (
        <FormControl fullWidth disabled={disabled} sx={selectIdleIconSx}>
          <InputLabel>{t('catalog.fields.defaultDuration')}</InputLabel>
          <Select {...field} label={t('catalog.fields.defaultDuration')}>
            {durationSelectOptions(field.value).map((minutes) => (
              <MenuItem key={minutes} value={String(minutes)}>
                {t('fields.durationMinutes', { count: minutes })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            autoFocus
            fullWidth
            disabled={disabled}
            label={t('catalog.form.name')}
            placeholder={t('catalog.placeholders.name')}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            labelEnd={
              <CharacterCount current={field.value.length} max={APPOINTMENT_TYPE_NAME_MAX_LENGTH} />
            }
            slotProps={{
              htmlInput: { maxLength: APPOINTMENT_TYPE_NAME_MAX_LENGTH },
            }}
          />
        )}
      />

      <Controller
        name="color"
        control={control}
        render={({ field }) => (
          <AppointmentTypeColorField
            value={field.value}
            onChange={field.onChange}
            disabled={disabled}
          />
        )}
      />

      {showPolicy ? (
        <Grid container spacing={2}>
          <Grid size={6}>{durationField}</Grid>
          <Grid size={6}>
            {isEdit ? (
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={disabled} sx={selectIdleIconSx}>
                    <InputLabel>{t('catalog.fields.status')}</InputLabel>
                    <Select
                      label={t('catalog.fields.status')}
                      value={field.value ? 'active' : 'inactive'}
                      onChange={(e) => field.onChange(e.target.value === 'active')}
                      renderValue={(selected) => (
                        <CatalogStatusOption
                          active={selected === 'active'}
                          label={
                            selected === 'active'
                              ? t('catalog.status.active')
                              : t('catalog.status.inactive')
                          }
                        />
                      )}
                    >
                      <MenuItem value="active">
                        <CatalogStatusOption active label={t('catalog.status.active')} />
                      </MenuItem>
                      <MenuItem value="inactive">
                        <CatalogStatusOption
                          active={false}
                          label={t('catalog.status.inactive')}
                        />
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            ) : (
              <FormControl fullWidth disabled sx={selectIdleIconSx}>
                <InputLabel>{t('catalog.fields.status')}</InputLabel>
                <Select
                  value="active"
                  label={t('catalog.fields.status')}
                  renderValue={() => (
                    <CatalogStatusOption active label={t('catalog.status.active')} />
                  )}
                >
                  <MenuItem value="active">
                    <CatalogStatusOption active label={t('catalog.status.active')} />
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>
      ) : (
        durationField
      )}

      {showPolicy ? (
        <>
          <Divider sx={{ mt: 0.5 }} />
          <Box>
            <Typography component="h6" variant="h6" sx={{ display: 'block', mb: 0.5 }}>
              {t('catalog.sections.policy')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('catalog.fields.policyHint')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="dentistOverlap"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth disabled={disabled} sx={selectIdleIconSx}>
                      <InputLabel>{t('catalog.fields.dentistOverlap')}</InputLabel>
                      <Select {...field} label={t('catalog.fields.dentistOverlap')}>
                        <MenuItem value="inherit">{t('catalog.policy.inherit')}</MenuItem>
                        <MenuItem value="block">{t('catalog.policy.block')}</MenuItem>
                        <MenuItem value="warn">{t('catalog.policy.warn')}</MenuItem>
                        <MenuItem value="allow">{t('catalog.policy.allow')}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="chairOverlap"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth disabled={disabled} sx={selectIdleIconSx}>
                      <InputLabel>{t('catalog.fields.chairOverlap')}</InputLabel>
                      <Select {...field} label={t('catalog.fields.chairOverlap')}>
                        <MenuItem value="inherit">{t('catalog.policy.inherit')}</MenuItem>
                        <MenuItem value="block">{t('catalog.policy.block')}</MenuItem>
                        <MenuItem value="warn">{t('catalog.policy.warn')}</MenuItem>
                        <MenuItem value="allow">{t('catalog.policy.allow')}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default AppointmentTypeFormFields;
